import * as THREE from "three/webgpu";
import type {ReferencePlane} from '../../reference';
import {SketchPointObject} from './SketchPointObject';
import type {AbstractSketchEdgeObject} from './AbstractSketchEdgeObject';
import {isAbstractSketchEdgeObject} from '../guards/isAbstractSketchEdgeObject';
import {SketchLineObject} from './SketchLineObject';
import {SketchArcObject,} from './SketchArcObject';
import {SketchCircleObject} from './SketchCircleObject';
import {SketchEllipseObject} from './SketchEllipseObject';
import {SketchSplineObject} from './SketchSplineObject';
import {SketchStyleSplineObject} from './SketchStyleSplineObject';
import {SketchSlotObject} from './SketchSlotObject';
import {SketchCollectionCreationSource, SketchCollectionObject} from './SketchCollectionObject';
import {type CollectSource, type ISketchObject, SketchPointKind} from './ISketchObject';
import {createSketchCloneContext, type SketchCloneContext} from './SketchCloneContext';
import {cloneMultiTargetConstraints, regenerateConstraintPersistentIds} from './SketchConstraintClone';
import type {SketchCircularPatternObject} from '../../sketch/circular-pattern/SketchCircularPatternObject';
import {isSketchPatternedConstraint} from '../../sketch/solver/constraint/relationDefinition/pattern/SketchPatternedConstraint';
import {
    createSketchEntityPersistentId,
    createSketchPersistentId,
} from '../../sketch/identity/SketchPersistentIdFactory';
import {isSketchEntityReferencePreview} from '../../reference/sketch-entity-reference/command/preview/SketchEntityReferencePreviewMarker';
import {
    isSketchMirrorAxisReferenceProvider,
    isSketchArcObject,
    isSketchCircleObject,
    isSketchCollectionObject,
    isSketchEllipseObject,
    isSketchGroup,
    isSketchLineObject,
    isSketchPointObject,
    isSketchSlotObject,
    isSketchSplineObject,
    isSketchStyleSplineObject,
} from '../guards';
import type {SketchMirrorAxisReferenceProvider} from '../../sketch/entities-transform/SketchMirrorAxisReferenceProvider';
import {SketchOverlayRenderStateApplier} from './SketchOverlayRenderStateApplier';
import {addObject3DToParent} from '../../utils/scene-object/addObject3DToParent';
import {
    SketchClosureCandidateFilter,
    SketchClosureEndpointCanonicalizer,
    SketchClosureResolver,
    type SketchClosureTraversalStep,
} from '../../sketch/closure';
import {markCadObjectAsRuntimeDerived} from '../../document/CadObjectPersistence';

/**
 * 草图组创建选项。
 */
export interface SketchGroupOptions {
    /**
     * 草图组显示名称。
     *
     * 设计说明：
     * 1. 草图组会被历史快照、运行时恢复和复制流程频繁克隆；
     * 2. 构造函数不能推进真实设计树编号，否则 Undo/Redo 快照会污染后续草图名称；
     * 3. 真实用户可见的默认命名由 SketchGroupManager 在创建入口集中分配。
     */
    name?: string;
    referencePlane: ReferencePlane;
}

/**
 * 草图定义变更事务元数据。
 *
 * 设计说明：
 * 1. 普通用户编辑只需要 `sourceObjectId`；
 * 2. 引用目标重建会一次影响多个目标实体，需要携带 changed/replaced 集合给下游引用定向传播；
 * 3. 这些字段只描述“本次草图定义事务”，不直接承载引用业务实现。
 */
export interface SketchDefinitionChangeMetadata {
    /** 触发源对象 ID。 */
    sourceObjectId?: string;
    /** 本次事务中被修改的草图对象 ID。 */
    changedObjectIds?: string[];
    /** 触发本轮传播的引用记录 ID，用于跳过当前批次已处理引用。 */
    originReferenceIds?: string[];
    /** 本轮传播 ID，用于把链式重建归并为同一事务。 */
    propagationId?: string;
    /** 替换重建时的旧对象到新对象映射。 */
    replacedObjectIds?: Array<{
        /** 被替换前的对象 ID。 */
        oldObjectId: string;
        /** 替换后的对象 ID。 */
        newObjectId: string;
    }>;
}

/**
 * 创建草图定义变更元数据快照。
 *
 * @param input 原始事务元数据。
 * @returns 与调用方可变数组隔离后的事件元数据。
 */
function cloneSketchDefinitionChangeMetadata(
    input: SketchDefinitionChangeMetadata,
): SketchDefinitionChangeMetadata {
    return {
        sourceObjectId: input.sourceObjectId,
        changedObjectIds: input.changedObjectIds ? [...input.changedObjectIds] : undefined,
        originReferenceIds: input.originReferenceIds ? [...input.originReferenceIds] : undefined,
        propagationId: input.propagationId,
        replacedObjectIds: input.replacedObjectIds?.map((item) => ({
            oldObjectId: item.oldObjectId,
            newObjectId: item.newObjectId,
        })),
    };
}

/**
 * 草图定义脏化事件。
 */
export interface SketchDefinitionDirtyEvent extends SketchDefinitionChangeMetadata {
    /** 事件类型。 */
    type: 'sketch-definition-dirty';
    /** 草图 ID。 */
    sketchId: string;
    /** 跨运行时稳定的草图持久 ID。 */
    persistentSketchId: string;
    /** 当前定义版本号。 */
    revision: number;
    /**
     * 当前定义变更的内部归属。
     * 该字段由 SketchGroup 从 owner 作用域生成，调用方不能通过普通 metadata 伪造。
     */
    definitionChangeOwnerId?: string;
    /** 变更原因。 */
    reason: string;
}

/**
 * 草图定义提交事件。
 */
export interface SketchDefinitionCommittedEvent extends SketchDefinitionChangeMetadata {
    /** 事件类型。 */
    type: 'sketch-definition-committed';
    /** 草图 ID。 */
    sketchId: string;
    /** 跨运行时稳定的草图持久 ID。 */
    persistentSketchId: string;
    /** 当前定义版本号。 */
    revision: number;
    /** 提交原因。 */
    reason: string;
}

/**
 * 草图实体移除事件。
 */
export interface SketchGroupEntityRemovedEvent {
    /** 事件类型。 */
    type: 'sketch-entity-removed';
    /** 草图 ID。 */
    sketchId: string;
    /** 跨运行时稳定的草图持久 ID。 */
    persistentSketchId: string;
    /** 被移除草图实体的稳定 ID；事件不得携带已离场的 Object3D。 */
    removedEntityIds: string[];
}


/**
 * 草图组内部跟踪动作枚举，与 MeshManager 的 SceneTreeListenAction 对称。
 */
enum SketchGroupTrackAction {
    /** 对象被添加到草图组子树。 */
    Add = 'add',
    /** 对象被从草图组子树移除。 */
    Remove = 'remove',
}

/**
 * 子对象事件载荷接口。
 */
interface ChildEvent {
    /** 发生变化的子对象。 */
    child: THREE.Object3D;
}

/**
 * 草图场景对象联合类型。
 */
type SketchSceneObject =
    | AbstractSketchEdgeObject
    | SketchPointObject
    | SketchSlotObject
    | SketchCollectionObject;



/**
 * 判断一个对象是否是 SketchGroup 实例。
 * @param obj 待判断对象。
 * @returns 是否为 SketchGroup。
 */
export {isSketchGroup} from '../guards/isSketchGroup';

/**
 * 草图默认命名由 SketchGroupManager 负责。
 *
 * 说明：
 * 1. SketchGroup 本身既会作为真实草图实体，也会作为历史快照副本；
 * 2. 构造函数不维护全局序号，避免快照 clone、Undo/Redo 恢复和临时预览污染设计树命名；
 * 3. 真实用户可见名称必须从草图管理器的创建入口分配。
 */

/**
 * 草图组：继承 THREE.Group，作为草图实体的容器。
 *
 * 职责：
 * 1. 持有关联的 ReferencePlane，提供 2D↔3D 坐标转换上下文；
 * 2. 管理草图实体的添加；
 * 3. 记录基准面 ID 和坐标系快照元数据；
 * 4. 监听自身子树的 childadded/childremoved 事件，自动维护分类型的草图对象集合；
 * 5. 在草图对象进入草图组时统一应用前景渲染状态，避免与宿主面共面时不可见。
 */
export class SketchGroup extends THREE.Group {
    /** 标识这是一个草图组。 */
    readonly isSketchGroup: boolean = true;

    /**
     * 跨会话稳定持久 ID（`cad:sketch:<uuid>` 格式）。
     * 说明：
     * 1. 与运行时 `uuid` 严格区分：`cloneSelf`（历史快照/恢复）继承该 ID；
     * 2. 复制出新草图的流程必须调用 `regeneratePersistentIdentity()` 重新生成整组身份。
     */
    public persistentId: string = createSketchPersistentId();

    /** 关联的基准面。 */
    private readonly _referencePlane: ReferencePlane;
    /** 草图定义版本号。 */
    private _definitionRevision = 0;
    /** 最近一次已提交版本号。 */
    private _lastCommittedRevision = 0;
    /** 当前定义变更归属标记，用于区分系统重建与外部编辑。 */
    private _activeDefinitionChangeOwnerId: string | null = null;
    /** 每个定义版本对应的变更归属标记。 */
    private readonly _definitionRevisionOwnerIdMap: Map<number, string | undefined> = new Map();
    /**
     * 运行态恢复期间删除事件抑制深度。
     *
     * 说明：历史恢复会先整体替换当前子树，再挂回快照副本。这个过程不是用户删除实体，
     * 不能触发引用关系删除和级联重建，否则 undo/redo 会把“恢复”误判为“删除”。
     */
    private _runtimeRestoreSuppressionDepth: number = 0;

    /** replacement/rollback 内部换壳期间抑制普通实体删除事件的嵌套深度。 */
    private _entityReplacementSuppressionDepth: number = 0;

    static  baseName = "草图"


    /** 已绑定事件监听的节点集合，防止重复绑定。 */
    private readonly _observedObjectSet: Set<THREE.Object3D> = new Set();

    /** 草图对象前景渲染状态应用器。 */
    private readonly _overlayRenderStateApplier: SketchOverlayRenderStateApplier = new SketchOverlayRenderStateApplier();

    /** 草图对象总集合。 */
    private readonly _sketchObjectSet: Set<SketchSceneObject> = new Set();

    /**
     * 不参与绘制、捕捉、轮廓与实体计数的约束引用目标。
     *
     * 外部镜像轴只为关系、持久化与重建提供稳定身份，不能进入通用草图实体域。
     */
    private readonly _sketchReferenceTargetSet: Set<SketchMirrorAxisReferenceProvider> = new Set();

    /** 草图点对象集合。 */
    private readonly _sketchPointObjectSet: Set<SketchPointObject> = new Set();

    /** 端点集合（isEndPoint === true 的点）。 */
    private readonly _endPointSet: Set<SketchPointObject> = new Set();

    /** 中点集合（isMidPoint === true 的点）。 */
    private readonly _midPointSet: Set<SketchPointObject> = new Set();

    /** 圆心点集合（isCenterPoint === true 的点）。 */
    private readonly _centerPointSet: Set<SketchPointObject> = new Set();

    /** 象限点集合（isQuadrantPoint === true 的点）。 */
    private readonly _quadrantPointSet: Set<SketchPointObject> = new Set();

    /** 用户草图点集合（pointKind === SketchPointKind.SketchPoint 的点）。 */
    private readonly _sketchPointKindSet: Set<SketchPointObject> = new Set();

    /** 草图边对象集合。 */
    private readonly _sketchEdgeObjectSet: Set<AbstractSketchEdgeObject> = new Set();

    /** 草图线对象集合。 */
    private readonly _sketchLineObjectSet: Set<SketchLineObject> = new Set();

    /** 草图圆弧对象集合。 */
    private readonly _sketchArcObjectSet: Set<SketchArcObject> = new Set();

    /** 草图圆对象集合。 */
    private readonly _sketchCircleObjectSet: Set<SketchCircleObject> = new Set();

    /** 草图椭圆对象集合。 */
    private readonly _sketchEllipseObjectSet: Set<SketchEllipseObject> = new Set();

    /** 草图样条曲线对象集合。 */
    private readonly _sketchSplineCurveObjectSet: Set<SketchSplineObject | SketchStyleSplineObject> = new Set();

    /** 草图槽口对象集合。 */
    private readonly _sketchSlotObjectSet: Set<SketchSlotObject> = new Set();

    /** 草图轮廓集合对象集合。 */
    private readonly _sketchCollectionObjectSet: Set<SketchCollectionObject> = new Set();


    /**
     * 持久 ID 到草图对象的索引。
     * 说明：
     * 1. 供尺寸/关系/持久化按稳定 ID 反查运行时对象；
     * 2. 同时承担“复制实体”场景的身份治愈：同一持久 ID 的第二个存活对象进组时自动换新 ID。
     */
    private readonly _persistentIdIndexMap: Map<string, SketchSceneObject> = new Map();

    /** 外部约束引用目标的独立持久身份索引。 */
    private readonly _referenceTargetPersistentIdIndexMap: Map<string, SketchMirrorAxisReferenceProvider> = new Map();


    /** 草图闭合候选过滤器。 */
    private readonly sketchClosureCandidateFilter: SketchClosureCandidateFilter = new SketchClosureCandidateFilter();
    /** 将闭合搜索中的坐标连接收口为真实共享端点。 */
    private readonly sketchClosureEndpointCanonicalizer: SketchClosureEndpointCanonicalizer
        = new SketchClosureEndpointCanonicalizer();


    /**
     * @param options 草图组创建选项。
     */
    constructor(options: SketchGroupOptions) {
        super();
        // 草图定义由 sketch extension 持久化，运行时显示子树不得重复进入模型 GLB/BRep 快照。
        markCadObjectAsRuntimeDerived(this);
        this.name = options.name ?? '';
        this._referencePlane = options.referencePlane;
        // 初始化自身子树监听，使草图组自动跟踪后代变化。
        this.bindSelfListeners();
    }

    /**
     * 获取关联的基准面。
     */
    get referencePlane(): ReferencePlane {
        return this._referencePlane;
    }


    /**
     * 获取当前草图定义版本号。
     * @returns 草图定义版本号。
     */
    public get definitionRevision(): number {
        return this._definitionRevision;
    }

    /**
     * 标记草图定义已变更。
     * @param input 变更上下文。
     */
    public markDefinitionDirty(input: {
        reason: string;
    } & SketchDefinitionChangeMetadata): void {
        const metadata = cloneSketchDefinitionChangeMetadata(input);
        const ownerId = this._activeDefinitionChangeOwnerId ?? undefined;
        this._definitionRevision += 1;
        this._definitionRevisionOwnerIdMap.set(this._definitionRevision, ownerId);
        (this as unknown as { dispatchEvent: (event: unknown) => void }).dispatchEvent({
            type: 'sketch-definition-dirty',
            sketchId: this.uuid,
            persistentSketchId: this.persistentId,
            revision: this._definitionRevision,
            definitionChangeOwnerId: ownerId,
            reason: input.reason,
            sourceObjectId: metadata.sourceObjectId,
            changedObjectIds: metadata.changedObjectIds,
            originReferenceIds: metadata.originReferenceIds,
            propagationId: metadata.propagationId,
            replacedObjectIds: metadata.replacedObjectIds,
        });
    }

    /**
     * 提交当前草图定义变更。
     * @param input 提交上下文。
     */
    public commitDefinitionChanges(input: {
        reason: string;
    } & SketchDefinitionChangeMetadata): void {
        if (this._definitionRevision <= this._lastCommittedRevision) {
            return;
        }
        const metadata = cloneSketchDefinitionChangeMetadata(input);
        this._lastCommittedRevision = this._definitionRevision;
        this.pruneCommittedDefinitionRevisionOwners();
        (this as unknown as { dispatchEvent: (event: unknown) => void }).dispatchEvent({
            type: 'sketch-definition-committed',
            sketchId: this.uuid,
            persistentSketchId: this.persistentId,
            revision: this._definitionRevision,
            reason: input.reason,
            sourceObjectId: metadata.sourceObjectId,
            changedObjectIds: metadata.changedObjectIds,
            originReferenceIds: metadata.originReferenceIds,
            propagationId: metadata.propagationId,
            replacedObjectIds: metadata.replacedObjectIds,
        });
    }

    /**
     * 判断当前草图是否存在尚未提交的定义变更。
     *
     * 设计说明：
     * 1. 草图会话结束时，底层需要据此决定是否触发一次统一提交；
     * 2. 该判断只关注版本游标，不关心宿主层当前 UI 状态；
     * 3. 通过草图组自报告，避免会话编排层再次窥探内部细节。
     *
     * @returns 是否存在待提交的定义变更。
     */
    public hasUncommittedDefinitionChanges(): boolean {
        return this._definitionRevision > this._lastCommittedRevision;
    }

    /**
     * 在指定 owner 作用域内执行定义变更。
     *
     * 设计说明：
     * 1. 引用重建会通过底层点位/边定义写入触发 dirty；
     * 2. 这些 dirty 需要与用户编辑或其他系统事务区分，防止后续引用提交误消费外部事务；
     * 3. 作用域采用栈式恢复，避免嵌套调用污染外层 owner。
     *
     * @param ownerId 定义变更归属标记。
     * @param work 需要执行的变更逻辑。
     * @returns work 的返回值。
     */
    public runDefinitionChangeOwnerScope<T>(ownerId: string, work: () => T): T {
        const previousOwnerId = this._activeDefinitionChangeOwnerId;
        this._activeDefinitionChangeOwnerId = ownerId;
        try {
            return work();
        } finally {
            this._activeDefinitionChangeOwnerId = previousOwnerId;
        }
    }

    /**
     * 在运行态快照恢复作用域内执行子树替换。
     *
     * @param work 需要执行的恢复逻辑。
     * @returns work 的返回值。
     */
    public runRuntimeRestoreScope<T>(work: () => T): T {
        this._runtimeRestoreSuppressionDepth += 1;
        try {
            return work();
        } finally {
            this._runtimeRestoreSuppressionDepth -= 1;
        }
    }

    /**
     * 在转换实体 replacement 或其回滚作用域内交换运行时对象。
     *
     * 该作用域只抑制实体删除事件，仍完整维护草图集合与持久身份索引。
     *
     * @param work 新旧运行时对象交换逻辑。
     * @returns work 的返回值。
     */
    public runEntityReplacementScope<T>(work: () => T): T {
        this._entityReplacementSuppressionDepth += 1;
        try {
            return work();
        } finally {
            this._entityReplacementSuppressionDepth -= 1;
        }
    }

    /**
     * 发布已随外层事务正式提交的真实实体删除。
     *
     * replacement 和回滚只做内部换壳，不调用该入口；派生真实删除由事务在全部资源
     * 收尾完成后统一调用，因此下游只会观察到完整正式状态。
     *
     * @param removedEntityIds 已永久离场的实体持久 ID。
     */
    public publishCommittedEntityRemoval(removedEntityIds: readonly string[]): void {
        const uniqueRemovedEntityIds = [...new Set(removedEntityIds)];
        if (uniqueRemovedEntityIds.length === 0) {
            return;
        }
        this.enqueueSketchEntityRemovedEvent(() => uniqueRemovedEntityIds);
    }

    /**
     * 判断某版本之后是否存在非指定 owner 产生的定义变更。
     *
     * @param revision 起始版本号，不包含该版本本身。
     * @param ownerId 允许的定义变更归属标记。
     * @returns 存在外部 owner 或未知 owner 的 dirty 时返回 true。
     */
    public hasDefinitionChangesOutsideOwnerSince(revision: number, ownerId: string): boolean {
        for (let currentRevision = revision + 1; currentRevision <= this._definitionRevision; currentRevision += 1) {
            if (this._definitionRevisionOwnerIdMap.get(currentRevision) !== ownerId) {
                return true;
            }
        }
        return false;
    }

    /**
     * 接受当前定义版本为已提交文档基线。
     *
     * 说明：
     * 1. 仅供外部重建已完成，或离线文档恢复已通过完整领域验证后对齐提交游标；
     * 2. 该操作只确认既有定义状态，不创建新的用户建模命令，因此不写入 Undo/Redo；
     * 3. 不派发 committed 事件，避免文档初始化或重建收口递归触发新一轮特征重建；
     * 4. 草图编辑产生的新定义仍必须通过 commitDefinitionChanges 提交，禁止用本方法绕过正常事务。
     */
    public acknowledgeDefinitionCommit(): void {
        this._lastCommittedRevision = this._definitionRevision;
        this.pruneCommittedDefinitionRevisionOwners();
    }

    /**
     * 清理已提交版本的 owner 记录。
     *
     * 说明：owner 只服务于“未提交事务边界”判断，提交后即可丢弃，避免长时间编辑导致 Map 无界增长。
     */
    private pruneCommittedDefinitionRevisionOwners(): void {
        Array.from(this._definitionRevisionOwnerIdMap.keys()).forEach((revision) => {
            if (revision <= this._lastCommittedRevision) {
                this._definitionRevisionOwnerIdMap.delete(revision);
            }
        });
    }

    /**
     * 复制当前草图组，返回与源对象完全解耦的新实例。
     * 说明：
     * 1. 草图组仅复制当前直接子对象，子对象内部层级由各自 `cloneSelf` 负责；
     * 2. 通过复制上下文保证跨对象共享点在副本中仍保持共享。
     * @param context 复制上下文。
     * @returns 当前草图组的独立副本。
     */
    public cloneSelf(context: SketchCloneContext = createSketchCloneContext()): SketchGroup {
        const clonedGroup = new SketchGroup({
            referencePlane: this.referencePlane,
        });

        // 复制草图组直接子对象（包含草图对象与非草图对象）。
        this.children.forEach((child) => {
            const clonedChild = this.cloneDirectChild(child, context);
            if (clonedChild.parent && clonedChild.parent !== clonedGroup) {
                console.trace('草图组复制得到的子对象已挂载到其他父节点，无法继续。');
            }
            if (clonedChild.parent !== clonedGroup) {
                clonedGroup.add(clonedChild);
            }
        });
        // 外部约束目标不属于 Object3D 子树，必须从独立目标域显式克隆。
        this._sketchReferenceTargetSet.forEach((referenceTarget) => {
            clonedGroup.addSketchReferenceTarget(referenceTarget.cloneSelf(context));
        });


        /**
         * 所有对象复制完成后，再统一复制多目标关系。
         * 原因：
         * 1. 多目标关系依赖完整的“源对象 -> 副本对象”映射；
         * 2. 若在对象复制中途绑定关系，会出现目标缺失。
         */
        const sourceConstraintObjects = this.collectConstraintSourceObjects();
        this.ensureConstraintObjectMapped(sourceConstraintObjects, context);
        cloneMultiTargetConstraints(
            sourceConstraintObjects,
            context.objectMap,
            context.shouldCloneConstraint,
        );


        // 副本表达同一逻辑草图（历史快照/恢复语义），必须继承持久身份；复制新草图的流程负责显式重新生成。
        clonedGroup.persistentId = this.persistentId;
        clonedGroup.position.copy(this.position);
        clonedGroup.quaternion.copy(this.quaternion);
        clonedGroup.scale.copy(this.scale);
        clonedGroup.visible = this.visible;
        clonedGroup.renderOrder = this.renderOrder;
        clonedGroup.frustumCulled = this.frustumCulled;
        clonedGroup.layers.mask = this.layers.mask;
        return clonedGroup;
    }

    /**
     * 复制草图组直接子对象。
     * @param child 原始子对象。
     * @param context 复制上下文。
     * @returns 子对象副本。
     */
    private cloneDirectChild(child: THREE.Object3D, context: SketchCloneContext): THREE.Object3D {
        if (isSketchGroup(child)) {
            return child.cloneSelf(context);
        }
        if (isSketchSlotObject(child)) {
            return child.cloneSelf(context);
        }
        if (isSketchCollectionObject(child)) {
            return child.cloneSelf(context);
        }
        if (isAbstractSketchEdgeObject(child)) {
            return child.cloneSelf(context);
        }
        if (isSketchPointObject(child)) {
            return child.cloneSelf(context);
        }

        // 非草图对象按 three.js 默认深复制，确保草图组下对象完整复制。
        return child.clone(true);
    }


    /**
     * 收集草图组内可承载约束的源对象。
     * 说明：
     * 1. 点、线状边与复合实体都可能直接承载关系；
     * 2. 只收集具备正式约束语义的对象，辅助显示对象仍被排除。
     * @returns 可复制关系的源对象列表。
     */
    private collectConstraintSourceObjects(): ISketchObject[] {
        const result: ISketchObject[] = [];
        this.getSketchObjects().forEach((object) => {
            // 辅助线、辅助控制点是派生显示对象，不参与正式草图关系复制。
            if (Reflect.get(object, 'isAuxiliaryObject') === true) {
                return;
            }
            if (
                isSketchPointObject(object)
                || isAbstractSketchEdgeObject(object)
                || isSketchSlotObject(object)
                || isSketchCollectionObject(object)
            ) {
                result.push(object);
            }
        });
        this._sketchReferenceTargetSet.forEach((referenceTarget) => {
            result.push(referenceTarget);
        });
        return result;
    }

    /**
     * 校验参与关系复制的对象均已建立副本映射。
     * @param sourceObjects 源对象列表。
     * @param context 复制上下文。
     */
    private ensureConstraintObjectMapped(
        sourceObjects: readonly ISketchObject[],
        context: SketchCloneContext,
    ): void {
        sourceObjects.forEach((sourceObject) => {
            if (!context.objectMap.has(sourceObject.uuid)) {
                console.trace(`草图组关系复制失败：对象副本映射缺失（uuid=${sourceObject.uuid}）。`,sourceObject);
            }
        });
    }


    /**
     * 获取草图对象总集合。
     * @returns 草图对象只读集合。
     */
    public getSketchObjects(): ReadonlySet<SketchSceneObject> {
        return this.filterPreviewObjects(this._sketchObjectSet);
    }

    /**
     * 获取不参与草图几何语义的约束引用目标。
     *
     * @returns 只读引用目标集合；调用方不得把它并入捕捉、轮廓或可绘制实体列表。
     */
    public getSketchReferenceTargets(): ReadonlySet<SketchMirrorAxisReferenceProvider> {
        return this._sketchReferenceTargetSet;
    }

    /**
     * 把外部约束引用目标挂入草图的独立目标域。
     *
     * @param referenceTarget 已在确认事务中创建、需要随关系持久化的引用目标。
     * @throws 引用目标已挂入场景、持久 ID 冲突、已释放或属于其它草图时抛出错误。
     */
    public addSketchReferenceTarget(referenceTarget: SketchMirrorAxisReferenceProvider): void {
        if (this._sketchReferenceTargetSet.has(referenceTarget)) {
            return;
        }
        if (referenceTarget.parent !== null) {
            throw new Error('草图外部约束引用目标不能挂入 Object3D 场景子树。');
        }
        const existingTarget = this._persistentIdIndexMap.get(referenceTarget.persistentId)
            ?? this._referenceTargetPersistentIdIndexMap.get(referenceTarget.persistentId);
        if (existingTarget && existingTarget !== referenceTarget) {
            // Provider 只在确认或持久化恢复后入组；静默换 ID 会让既有关系 targetIds 指向错误对象。
            throw new Error(`草图外部约束引用目标持久 ID 冲突: ${referenceTarget.persistentId}`);
        }
        referenceTarget.bindSketchReferenceTargetOwner(this);
        this._sketchReferenceTargetSet.add(referenceTarget);
        this._referenceTargetPersistentIdIndexMap.set(referenceTarget.persistentId, referenceTarget);
    }

    /**
     * 从草图移除外部约束引用目标。
     *
     * @param referenceTarget 已失去最后一条关系或事务回滚的引用目标。
     */
    public removeSketchReferenceTarget(referenceTarget: SketchMirrorAxisReferenceProvider): void {
        if (!this._sketchReferenceTargetSet.delete(referenceTarget)) {
            return;
        }
        if (this._referenceTargetPersistentIdIndexMap.get(referenceTarget.persistentId) === referenceTarget) {
            this._referenceTargetPersistentIdIndexMap.delete(referenceTarget.persistentId);
        }
        referenceTarget.unbindSketchReferenceTargetOwner(this);
    }

    /**
     * 判断外部约束目标是否由当前草图正式持有。
     *
     * @param referenceTarget 待判断的外部约束目标。
     * @returns 目标已进入当前草图主状态时返回 true。
     */
    public hasSketchReferenceTarget(referenceTarget: SketchMirrorAxisReferenceProvider): boolean {
        return this._sketchReferenceTargetSet.has(referenceTarget);
    }

    /**
     * 获取草图点对象集合。
     * @returns 草图点对象只读集合。
     */
    public getSketchPointObjects(): ReadonlySet<SketchPointObject> {
        return this.filterPreviewObjects(this._sketchPointObjectSet);
    }

    /**
     * 获取端点集合。
     * @returns 端点只读集合。
     */
    public getEndPoints(): ReadonlySet<SketchPointObject> {
        return this.filterPreviewObjects(this._endPointSet);
    }

    /**
     * 获取中点集合。
     * @returns 中点只读集合。
     */
    public getMidPoints(): ReadonlySet<SketchPointObject> {
        return this.filterPreviewObjects(this._midPointSet);
    }

    /**
     * 获取圆心点集合。
     * @returns 圆心点只读集合。
     */
    public getCenterPoints(): ReadonlySet<SketchPointObject> {
        return this.filterPreviewObjects(this._centerPointSet);
    }

    /**
     * 获取象限点集合。
     * @returns 象限点只读集合。
     */
    public getQuadrantPoints(): ReadonlySet<SketchPointObject> {
        return this.filterPreviewObjects(this._quadrantPointSet);
    }

    /**
     * 获取用户草图点集合（SketchPointKind.SketchPoint）。
     * @returns 用户草图点只读集合。
     */
    public getSketchKindPoints(): ReadonlySet<SketchPointObject> {
        return this._sketchPointKindSet;
    }

    /**
     * 获取草图边对象集合。
     * @returns 草图边对象只读集合。
     */
    public getSketchEdgeObjects(): ReadonlySet<AbstractSketchEdgeObject> {
        return this.filterPreviewObjects(this._sketchEdgeObjectSet);
    }

    /**
     * 获取草图线对象集合。
     * @returns 草图线对象只读集合。
     */
    public getSketchLineObjects(): ReadonlySet<SketchLineObject> {
        return this.filterPreviewObjects(this._sketchLineObjectSet);
    }

    /**
     * 获取草图圆弧对象集合。
     * @returns 草图圆弧对象只读集合。
     */
    public getSketchArcObjects(): ReadonlySet<SketchArcObject> {
        return this.filterPreviewObjects(this._sketchArcObjectSet);
    }

    /**
     * 获取草图圆对象集合。
     * @returns 草图圆对象只读集合。
     */
    public getSketchCircleObjects(): ReadonlySet<SketchCircleObject> {
        return this.filterPreviewObjects(this._sketchCircleObjectSet);
    }

    /**
     * 获取草图椭圆对象集合。
     * @returns 草图椭圆对象只读集合。
     */
    public getSketchEllipseObjects(): ReadonlySet<SketchEllipseObject> {
        return this.filterPreviewObjects(this._sketchEllipseObjectSet);
    }

    /**
     * 获取草图样条曲线对象集合。
     * @returns 草图样条曲线对象只读集合。
     */
    public getSketchSplineCurveObjects(): ReadonlySet<SketchSplineObject | SketchStyleSplineObject> {
        return this.filterPreviewObjects(this._sketchSplineCurveObjectSet);
    }

    /**
     * 获取草图槽口对象集合。
     * @returns 草图槽口对象只读集合。
     */
    public getSketchSlotObjects(): ReadonlySet<SketchSlotObject> {
        return this.filterPreviewObjects(this._sketchSlotObjectSet);
    }

    /**
     * 过滤掉挂在草图组中的临时 preview 对象。
     * 说明：
     * 1. Convert Entities preview 会临时挂入 `targetSketch` 以复用现有渲染链；
     * 2. 这些对象不能重新进入草图组对外暴露的“正式对象集合”，否则点捕捉、修剪、绘制辅助等直接扫集合的旧路径会误把 preview 当成正式草图实体；
     * 3. 这里统一在 getter 层收口，避免各策略散落重复过滤。
     *
     * @param objects 原始草图对象集合。
     * @returns 已排除 preview 的只读集合快照。
     */
    private filterPreviewObjects<TObject extends THREE.Object3D>(
        objects: ReadonlySet<TObject>,
    ): ReadonlySet<TObject> {
        const filteredObjects = new Set<TObject>();
        objects.forEach((object) => {
            if (isSketchEntityReferencePreview(object)) {
                return;
            }
            filteredObjects.add(object);
        });
        return filteredObjects;
    }

    /**
     * 获取草图轮廓集合对象集合。
     * @returns 草图轮廓集合对象只读集合。
     */
    public getSketchCollectionObjects(): ReadonlySet<SketchCollectionObject> {
        return this._sketchCollectionObjectSet;
    }


    /**
     * 收集当前草图组内仍可参与闭合解析的集合源。
     *
     * 说明：
     * 1. 闭合候选过滤规则由草图组统一持有，外部删除、裁剪和绘制链路不应复制判断；
     * 2. 删除草图实体后，旧集合绑定可能已经释放，此方法读取的是删除后的最新运行时对象图谱；
     * 3. 返回值只表示“可尝试闭合”，真正是否闭合仍由 `tryFinalizeClosedSketchCollection(...)` 判定。
     *
     * @returns 当前草图组内可参与闭合解析的集合源列表。
     */
    public collectAvailableSketchClosureSources(): CollectSource[] {
        return this.sketchClosureCandidateFilter.collectAvailableSources(
            Array.from(this.getSketchEdgeObjects()),
        );
    }


    /**
     * 基于草图组当前对象解析可闭合的有序集合源链。
     *
     * 说明：
     * 1. 草图组掌握当前草图内全部边、端点与集合归属，是闭合判断的统一入口；
     * 2. 调用方只提供本次操作直接影响的种子边，候选边统一从当前草图组中收集；
     * 3. 已归属闭合集合或槽口的边视为锁定，开链集合中的边允许继续参与新的闭合收口；
     * 4. 真正的链路遍历委托 `SketchClosureResolver`，避免绘制、Trim 与轮廓采样重复实现闭合算法。
     *
     * @param seedSources 本次绘制、裁剪或编辑直接影响的种子边。
     * @returns 可闭合时返回有序集合源链；无法闭合时返回 null。
     */
    public resolveClosedSketchSources(seedSources: readonly CollectSource[]): CollectSource[] | null {
        const traversalSteps = this.resolveClosedSketchTraversal(seedSources);
        return traversalSteps?.map((step) => step.source) ?? null;
    }

    /**
     * 基于草图组当前对象解析可闭合的有向遍历链。
     *
     * 说明：
     * 1. 闭合集合落地既需要有序源对象，也需要每条边的实际遍历方向；
     * 2. 端点统一器据此判断连接处对应源边起点还是终点；
     * 3. 候选过滤与闭合搜索继续集中在草图组入口，调用方不感知场景树细节。
     *
     * @param seedSources 本次绘制、裁剪或编辑直接影响的种子边。
     * @returns 可闭合时返回有向遍历链；无法闭合时返回 null。
     */
    private resolveClosedSketchTraversal(
        seedSources: readonly CollectSource[],
    ): SketchClosureTraversalStep[] | null {
        if (seedSources.length < 1) {
            return null;
        }
        if (seedSources.some((source) => !this.sketchClosureCandidateFilter.isCollectSourceAvailableForSketchClosure(source))) {
            return null;
        }

        const candidateSources = this.sketchClosureCandidateFilter.collectAvailableSources(
            Array.from(this.getSketchEdgeObjects()),
        );
        const traversalSteps = SketchClosureResolver.resolveClosedGeometryTraversal(seedSources, candidateSources);
        if (!traversalSteps || traversalSteps.length < 2) {
            return null;
        }
        return traversalSteps;
    }

    /**
     * 基于种子边尝试创建闭合集合。
     *
     * 说明：
     * 1. 该方法复用 `resolveClosedSketchSources(...)` 的统一闭合判断；
     * 2. 收口前会释放旧开链集合绑定，避免同一条边同时挂在旧开链集合与新闭合集合上；
     * 3. 创建集合后会把共享端点重挂到集合下，保持后续拖拽、选择与历史快照语义一致。
     *
     * @param seedSources 本次操作的种子边。
     * @param options 收口选项。
     * @returns 成功创建的闭合集合；无法闭合时返回 null。
     */
    public tryFinalizeClosedSketchCollection(
        seedSources: readonly CollectSource[],
        options: {syncContour: boolean} = {syncContour: false},
    ): SketchCollectionObject | null {
        const resolvedTraversal = this.resolveClosedSketchTraversal(seedSources);
        if (!resolvedTraversal) {
            return null;
        }

        // 闭合解析允许坐标兜底，正式集合必须把相邻边收口到同一端点对象，保证后续拖动不破坏拓扑。
        this.sketchClosureEndpointCanonicalizer.canonicalize(resolvedTraversal);
        const resolvedSources = resolvedTraversal.map((step) => step.source);

        this.releaseOpenCollectionBindingsForSketchClosure(resolvedSources);

        const collection = new SketchCollectionObject({
            sources: [...resolvedSources],
            referencePlane: this.referencePlane,
            creationSource: SketchCollectionCreationSource.Generic,
        });
        if (options.syncContour) {
            collection.syncContour();
        }

        const closedSourceIdSet = new Set(resolvedSources.map((source) => source.uuid));

        const endpointUsageCountByPointId = this.countSketchClosureEndpointUsages(resolvedSources);

        this.resolveSketchClosureSourceControlPoints(resolvedSources).forEach((point) => {
            const parentUuid = point.parent?.uuid ?? null;
            if (
                parentUuid
                && closedSourceIdSet.has(parentUuid)
                && !this.shouldPromoteSketchClosureControlPoint(point, endpointUsageCountByPointId)
            ) {
                return;
            }
            if (point.parent !== collection) {
                addObject3DToParent(point, collection);
            }
        });

        addObject3DToParent(collection, this);
        return collection;
    }

    /**
     * 解析闭合源涉及的唯一端点。
     *
     * @param sources 闭合源链。
     * @returns 唯一端点列表。
     */
    private resolveSketchClosureSourceControlPoints(sources: readonly CollectSource[]): SketchPointObject[] {
        const pointMap = new Map<string, SketchPointObject>();
        sources.forEach((source) => {
            const {startPoint, endPoint} = SketchClosureResolver.getSourceEndpoints(source);
            pointMap.set(startPoint.uuid, startPoint);
            pointMap.set(endPoint.uuid, endPoint);
        });
        return [...pointMap.values()];
    }


    /**
     * 统计闭合链端点对象被多少条源边引用。
     *
     * 说明：
     * 1. 圆弧会托管无父级端点，线段可继续复用这些端点形成闭合轮廓；
     * 2. 若共享端点仍留在圆弧子树中，后续强力裁剪删除圆弧会把线段端点一起带离场景；
     * 3. 因此闭合集合收口时需要识别被多条源边共享的端点，并将其提升到集合层级。
     *
     * @param sources 闭合链源边。
     * @returns 点 ID 到端点引用次数的映射。
     */
    private countSketchClosureEndpointUsages(sources: readonly CollectSource[]): Map<string, number> {
        const usageCountByPointId = new Map<string, number>();

        sources.forEach((source) => {
            const {startPoint, endPoint} = SketchClosureResolver.getSourceEndpoints(source);
            [startPoint, endPoint].forEach((point) => {
                usageCountByPointId.set(point.uuid, (usageCountByPointId.get(point.uuid) ?? 0) + 1);
            });
        });

        return usageCountByPointId;
    }

    /**
     * 判断闭合端点是否需要提升到集合层级。
     *
     * @param point 待检查端点。
     * @param endpointUsageCountByPointId 端点引用次数索引。
     * @returns 需要提升返回 true。
     */
    private shouldPromoteSketchClosureControlPoint(
        point: SketchPointObject,
        endpointUsageCountByPointId: ReadonlyMap<string, number>,
    ): boolean {
        return (endpointUsageCountByPointId.get(point.uuid) ?? 0) > 1;
    }


    /**
     * 创建新闭合集合前释放旧开链集合绑定。
     *
     * @param sources 即将并入新闭合集合的源链。
     */
    private releaseOpenCollectionBindingsForSketchClosure(sources: readonly CollectSource[]): void {
        const staleOpenCollections = new Set<SketchCollectionObject>();

        sources.forEach((source) => {
            const closedCollection = Reflect.get(source, 'closedCollection');
            if (!(closedCollection instanceof SketchCollectionObject)) {
                return;
            }
            if (closedCollection.isClosedGeometry) {
                return;
            }
            source.detachClosedCollection(closedCollection);
            staleOpenCollections.add(closedCollection);
        });

        staleOpenCollections.forEach((openCollection) => {
            const remainedSources = Array.from(openCollection.collectSet)
                .filter((candidate) => Reflect.get(candidate, 'closedCollection') === openCollection);
            if (remainedSources.length >= 2) {
                openCollection.setSources(remainedSources);
                return;
            }
            openCollection.setSources([]);
            if (openCollection.parent) {
                openCollection.parent.remove(openCollection);
            }
        });
    }


    /**
     * 释放草图组：解绑所有已观察节点的事件监听，清空所有跟踪集合。
     */
    public dispose(): void {
        // 引用目标不在场景树中，必须由独立目标域显式解除关系并释放 resolver。
        Array.from(this._sketchReferenceTargetSet).forEach((referenceTarget) => {
            this.removeSketchReferenceTarget(referenceTarget);
            referenceTarget.dispose();
        });
        // 遍历副本，避免 unobserveSingleObject 修改集合时影响迭代。
        Array.from(this._observedObjectSet).forEach((object) => {
            this.unobserveSingleObject(object);
        });
        this.clearTrackedSets();
    }

    /** 子对象添加事件处理器。 */
    private readonly handleChildAdded = (event: ChildEvent): void => {
        // 新子树一旦进入草图组，就必须立即切到草图前景渲染层，避免与宿主面共面时被遮挡。
        this._overlayRenderStateApplier.apply(event.child);
        // 渲染状态就位后再绑定监听并更新跟踪集合，确保后续交互读取到的就是最终展示状态。
        this.observeObjectTree(event.child);
        this.trackSketchObjectTree(event.child, SketchGroupTrackAction.Add);
    };

    /** 子对象移除事件处理器。 */
    private readonly handleChildRemoved = (event: ChildEvent): void => {
        const removedObjects = this.collectSketchObjectsFromTree(event.child);
        // 先从跟踪集合移除，再解绑监听。
        this.trackSketchObjectTree(event.child, SketchGroupTrackAction.Remove);
        this.unobserveObjectTree(event.child);
        this.scheduleSketchEntityRemovedEvent(removedObjects);
    };

    /**
     * 绑定自身子树监听，从草图组自身开始观察整棵子树。
     */
    private bindSelfListeners(): void {
        this.observeObjectTree(this);
    }

    /**
     * 递归监听对象树中每个节点。
     * @param root 起始根节点。
     */
    private observeObjectTree(root: THREE.Object3D): void {
        root.traverse((object) => {
            this.observeSingleObject(object);
        });
    }

    /**
     * 递归取消监听对象树中每个节点。
     * @param root 起始根节点。
     */
    private unobserveObjectTree(root: THREE.Object3D): void {
        root.traverse((object) => {
            this.unobserveSingleObject(object);
        });
    }

    /**
     * 监听单个节点的 childadded / childremoved 事件。
     * @param object 目标节点。
     */
    private observeSingleObject(object: THREE.Object3D): void {
        // 已监听过则跳过，防止重复绑定。
        if (this._observedObjectSet.has(object)) {
            return;
        }
        object.addEventListener(
            'childadded',
            this.handleChildAdded as unknown as THREE.EventListener<{ child: THREE.Object3D }, 'childadded', THREE.Object3D<THREE.Object3DEventMap>>,
        );
        object.addEventListener(
            'childremoved',
            this.handleChildRemoved as unknown as THREE.EventListener<{ child: THREE.Object3D }, 'childremoved', THREE.Object3D<THREE.Object3DEventMap>>,
        );
        this._observedObjectSet.add(object);
    }

    /**
     * 取消监听单个节点，并从已观察集合中移除。
     * @param object 目标节点。
     */
    private unobserveSingleObject(object: THREE.Object3D): void {
        if (!this._observedObjectSet.has(object)) {
            return;
        }
        object.removeEventListener(
            'childadded',
            this.handleChildAdded as unknown as THREE.EventListener<{ child: THREE.Object3D }, 'childadded', THREE.Object3D<THREE.Object3DEventMap>>,
        );
        object.removeEventListener(
            'childremoved',
            this.handleChildRemoved as unknown as THREE.EventListener<{ child: THREE.Object3D }, 'childremoved', THREE.Object3D<THREE.Object3DEventMap>>,
        );
        this._observedObjectSet.delete(object);
    }

    /**
     * 遍历对象树，对每个节点执行草图跟踪操作。
     * @param root 起始根节点。
     * @param action 跟踪动作（添加/移除）。
     */
    private trackSketchObjectTree(root: THREE.Object3D, action: SketchGroupTrackAction): void {
        root.traverse((object) => {
            this.trackSingleSketchObject(object, action);
        });
    }

    /**
     * 从指定对象树收集草图实体对象。
     *
     * 设计说明：
     * 1. 删除入口可能来自键盘、修剪、属性面板或后续右键菜单；
     * 2. 引用系统需要统一感知“哪些草图实体离场”，不能耦合到某一个入口；
     * 3. 因此在 SketchGroup 的子树监听层收口，确保所有 Object3D 移除路径都能发布同一事件。
     *
     * @param root 起始根节点。
     * @returns 被移除对象树中的草图实体集合。
     */
    private collectSketchObjectsFromTree(root: THREE.Object3D): SketchSceneObject[] {
        const removedObjects: SketchSceneObject[] = [];
        root.traverse((object) => {
            if (isSketchEntityReferencePreview(object)) {
                return;
            }
            if (
                isSketchPointObject(object)
                || isAbstractSketchEdgeObject(object)
                || isSketchSlotObject(object)
                || isSketchCollectionObject(object)
            ) {
                removedObjects.push(object);
            }
        });
        return removedObjects;
    }

    /**
     * 延后发布草图实体移除事件。
     *
     * 设计说明：
     * 1. `SketchCollectionObject.setSources()` 会先移除旧边再把保留边重新挂回；
     * 2. 这类“同一同步事务内的重挂载”不是删除，不能触发引用级联；
     * 3. 因此延后一轮微任务并过滤已重新进入当前草图组的对象，只发布真实离场实体。
     *
     * @param removedObjects 被移除的草图实体集合。
     */
    private scheduleSketchEntityRemovedEvent(
        removedObjects: SketchSceneObject[],
    ): void {
        if (
            this._runtimeRestoreSuppressionDepth > 0
            || this._entityReplacementSuppressionDepth > 0
        ) {
            return;
        }
        if (removedObjects.length === 0) {
            return;
        }
        this.enqueueSketchEntityRemovedEvent(() => removedObjects
            .filter((object) => !this.isObjectInsideSketchGroup(object))
            .map((object) => object.persistentId));
    }

    /**
     * 延后一轮微任务发布去重后的实体删除 ID。
     *
     * @param resolveRemovedEntityIds 发布时读取真实删除实体 ID 的函数。
     */
    private enqueueSketchEntityRemovedEvent(
        resolveRemovedEntityIds: () => readonly string[],
    ): void {
        const publish = (): void => {
            const removedEntityIds = [...new Set(resolveRemovedEntityIds())];
            if (removedEntityIds.length === 0) {
                return;
            }
            (this as unknown as { dispatchEvent: (event: unknown) => void }).dispatchEvent({
                type: 'sketch-entity-removed',
                sketchId: this.uuid,
                persistentSketchId: this.persistentId,
                removedEntityIds,
            });
        };

        if (typeof globalThis.queueMicrotask === 'function') {
            globalThis.queueMicrotask(publish);
            return;
        }
        void Promise.resolve().then(publish);
    }

    /**
     * 判断对象当前是否仍在本草图组子树内。
     *
     * @param object 目标对象。
     * @returns 对象仍挂在当前草图组内时返回 true。
     */
    private isObjectInsideSketchGroup(object: THREE.Object3D): boolean {
        let current: THREE.Object3D | null = object;
        while (current) {
            if (current === this) {
                return true;
            }
            current = current.parent;
        }
        return false;
    }

    /**
     * 对单个节点进行分类跟踪，将其加入或移出对应类型集合。
     * @param object 目标节点。
     * @param action 跟踪动作（添加/移除）。
     */
    private trackSingleSketchObject(object: THREE.Object3D, action: SketchGroupTrackAction): void {

        // 身份索引先于分类集合维护，保证“复制实体”场景在对象进组瞬间完成身份治愈。
        this.trackPersistentIdentity(object, action);

        if (isSketchPointObject(object)) {
            this.updateSetByAction(this._sketchPointObjectSet, object, action);
            this.updateSetByAction(this._sketchObjectSet, object, action);

            // 按点角色分类到对应子集合。
            if (object.isEndPoint) {
                this.updateSetByAction(this._endPointSet, object, action);
            }
            if (object.isMidPoint) {
                this.updateSetByAction(this._midPointSet, object, action);
            }
            if (object.isCenterPoint) {
                this.updateSetByAction(this._centerPointSet, object, action);
            }
            if (object.isQuadrantPoint) {
                this.updateSetByAction(this._quadrantPointSet, object, action);
            }
            if (object.pointKind === SketchPointKind.SketchPoint) {
                this.updateSetByAction(this._sketchPointKindSet, object, action);
            }

            return;
        }

        if (isSketchSlotObject(object)) {
            this.updateSetByAction(this._sketchSlotObjectSet, object, action);
            this.updateSetByAction(this._sketchObjectSet, object, action);
            return;
        }

        if (isSketchCollectionObject(object)) {
            this.updateSetByAction(this._sketchCollectionObjectSet, object, action);
            this.updateSetByAction(this._sketchObjectSet, object, action);
            return;
        }

        if (isAbstractSketchEdgeObject(object)) {
            this.updateSetByAction(this._sketchEdgeObjectSet, object, action);
            this.updateSetByAction(this._sketchObjectSet, object, action);

            // 进一步细分具体边类型集合。
            if (isSketchLineObject(object)) {
                this.updateSetByAction(this._sketchLineObjectSet, object, action);
            }
            if (isSketchArcObject(object)) {
                this.updateSetByAction(this._sketchArcObjectSet, object, action);
            }
            if (isSketchCircleObject(object)) {
                this.updateSetByAction(this._sketchCircleObjectSet, object, action);
            }
            if (isSketchEllipseObject(object)) {
                this.updateSetByAction(this._sketchEllipseObjectSet, object, action);
            }
            if (isSketchSplineObject(object) || isSketchStyleSplineObject(object)) {
                this.updateSetByAction(this._sketchSplineCurveObjectSet, object, action);
            }
        }
    }

    /**
     * 根据动作将对象加入或移出目标集合。
     * @param set 目标集合。
     * @param object 目标对象。
     * @param action 跟踪动作（添加/移除）。
     */

    /**
     * 维护持久 ID 索引并执行身份治愈。
     * 说明：
     * 1. 历史快照恢复（整组替换）中副本继承原 ID，新组索引从零重建，不会触发治愈；
     * 2. “复制实体”把携带源 ID 的副本加入同一草图组时，源对象仍存活于组内，
     *    此时为副本分配全新 ID，保证组内持久身份唯一；
     * 3. 移除时仅当索引仍指向该对象才清除，避免误删治愈后接管同 ID 的对象。
     * @param object 目标节点。
     * @param action 跟踪动作（添加/移除）。
     */
    private trackPersistentIdentity(object: THREE.Object3D, action: SketchGroupTrackAction): void {
        if (
            !isSketchPointObject(object)
            && !isSketchSlotObject(object)
            && !isSketchCollectionObject(object)
            && !isAbstractSketchEdgeObject(object)
        ) {
            return;
        }
        const sketchObject = object as ISketchObject;
        if (action === SketchGroupTrackAction.Add) {
            const existingObject = this._persistentIdIndexMap.get(sketchObject.persistentId)
                ?? this._referenceTargetPersistentIdIndexMap.get(sketchObject.persistentId);
            if (
                existingObject
                && existingObject !== sketchObject
                && this.isObjectInsideSketchGroup(existingObject)
            ) {
                // 同 ID 的第二个存活对象是“复制出的新实体”，必须换新身份。
                sketchObject.persistentId = createSketchEntityPersistentId();
            }
            this._persistentIdIndexMap.set(sketchObject.persistentId, sketchObject as SketchSceneObject);
            return;
        }
        if (this._persistentIdIndexMap.get(sketchObject.persistentId) === sketchObject) {
            this._persistentIdIndexMap.delete(sketchObject.persistentId);
        }
    }

    /**
     * 按持久 ID 查找组内草图对象。
     * @param persistentId 稳定持久 ID。
     * @returns 命中的草图对象；不存在时返回 null。
     */
    public getObjectByPersistentId(persistentId: string): SketchSceneObject | null {
        return this._persistentIdIndexMap.get(persistentId) ?? null;
    }

    /**
     * 按持久 ID 查找关系可使用的草图实体或外部引用目标。
     *
     * @param persistentId 关系 targetIds 中的稳定身份。
     * @returns 组内实体或引用目标；未命中时返回 null。
     */
    public getConstraintTargetByPersistentId(persistentId: string): ISketchObject | null {
        return this._persistentIdIndexMap.get(persistentId)
            ?? this._referenceTargetPersistentIdIndexMap.get(persistentId)
            ?? null;
    }

    /**
     * 按运行时 uuid 解析草图对象的持久 ID。
     * 说明：
     * 1. 供“会话内运行时句柄 → 跨会话业务身份”的边界转换使用（如特征绑定构建）；
     * 2. 未命中（非本组对象或派生显示对象）时返回 null，由调用方决定回退策略。
     * @param runtimeUuid 运行时 uuid。
     * @returns 持久 ID；未命中时返回 null。
     */
    public resolvePersistentIdByRuntimeUuid(runtimeUuid: string): string | null {
        let resolvedPersistentId: string | null = null;
        this._persistentIdIndexMap.forEach((sketchObject, persistentId) => {
            if (resolvedPersistentId === null && sketchObject.uuid === runtimeUuid) {
                resolvedPersistentId = persistentId;
            }
        });
        this._referenceTargetPersistentIdIndexMap.forEach((referenceTarget, persistentId) => {
            if (resolvedPersistentId === null && referenceTarget.uuid === runtimeUuid) {
                resolvedPersistentId = persistentId;
            }
        });
        return resolvedPersistentId;
    }

    /**
     * 为当前草图组及其全部实体、几何关系重新生成持久身份。
     * 说明：
     * 1. 供“复制出新逻辑草图”的流程调用（如设计树复制草图）；
     * 2. `cloneSelf` 继承的旧身份会与源草图冲突，必须整组换新；
     * 3. 索引同步重建，几何关系经 `regenerateConstraintPersistentIds` 去重换新；
     * 4. 返回值只服务当前复制事务的尺寸引用重映射，调用方不得持久保存。
     *
     * @returns 源持久 ID 到新持久 ID 的临时映射。
     */
    public regeneratePersistentIdentity(): ReadonlyMap<string, string> {
        const persistentIdMap = new Map<string, string>();
        const sourceSketchId = this.persistentId;

        this.persistentId = createSketchPersistentId();
        persistentIdMap.set(sourceSketchId, this.persistentId);
        this._persistentIdIndexMap.clear();
        this._referenceTargetPersistentIdIndexMap.clear();
        const trackedObjects = this.collectPersistentIdentityDomainObjects();
        trackedObjects.forEach((trackedObject) => {
            const sourcePersistentId = trackedObject.persistentId;

            trackedObject.persistentId = createSketchEntityPersistentId();
            if (isSketchMirrorAxisReferenceProvider(trackedObject)) {
                trackedObject.regenerateReferenceIdentity();
            }
            persistentIdMap.set(sourcePersistentId, trackedObject.persistentId);
            if (isSketchMirrorAxisReferenceProvider(trackedObject)) {
                this._referenceTargetPersistentIdIndexMap.set(trackedObject.persistentId, trackedObject);
            } else {
                this._persistentIdIndexMap.set(trackedObject.persistentId, trackedObject as SketchSceneObject);
            }
        });
        this.regenerateCircularPatternPersistentIdentities(persistentIdMap, trackedObjects);
        regenerateConstraintPersistentIds(
            trackedObjects.filter((trackedObject): trackedObject is AbstractSketchEdgeObject | SketchPointObject | SketchMirrorAxisReferenceProvider => (
                isSketchPointObject(trackedObject)
                || isAbstractSketchEdgeObject(trackedObject)
                || isSketchMirrorAxisReferenceProvider(trackedObject)
            )),
        );
        return persistentIdMap;
    }

    /**
     * 在整草图复制事务中换新圆周阵列定义及其 Patterned 关系归属。
     *
     * 处理顺序固定为：阵列定义重映射实体引用 → 集合元数据重映射阵列 ID →
     * Patterned 关系重映射阵列归属。这样任何持久化或参数编辑入口看到的都是同一身份域。
     *
     * @param persistentIdMap 当前复制事务的可写持久 ID 映射。
     * @param trackedObjects 已完成实体身份换新的草图领域对象。
     */
    private regenerateCircularPatternPersistentIdentities(
        persistentIdMap: Map<string, string>,
        trackedObjects: readonly ISketchObject[],
    ): void {
        const patternObjects: SketchCircularPatternObject[] = [];
        this.traverse((object) => {
            if (Reflect.get(object, 'isSketchCircularPatternObject') === true) {
                patternObjects.push(object as SketchCircularPatternObject);
            }
        });

        // 阵列源和实例均依赖实体映射，因此必须在普通草图对象换新完成后处理。
        patternObjects.forEach((patternObject) => {
            patternObject.regeneratePersistentIdentity(persistentIdMap);
        });

        this.getSketchCollectionObjects().forEach((collection) => {
            const sourcePatternPersistentId = collection.geometryData?.circularPatternPersistentId;
            if (typeof sourcePatternPersistentId !== 'string') {
                return;
            }
            const remappedPatternPersistentId = persistentIdMap.get(sourcePatternPersistentId);
            if (!remappedPatternPersistentId) {
                throw new Error(`草图复制失败：集合引用的圆周阵列不存在 ${sourcePatternPersistentId}。`);
            }
            collection.geometryData = {
                ...collection.geometryData,
                circularPatternPersistentId: remappedPatternPersistentId,
            };
        });

        const visitedRelations = new Set<object>();
        trackedObjects.forEach((trackedObject) => {
            if (!isSketchPointObject(trackedObject) && !isAbstractSketchEdgeObject(trackedObject)) {
                return;
            }
            trackedObject.constraints.getRelations().forEach((relation) => {
                if (visitedRelations.has(relation)) {
                    return;
                }
                visitedRelations.add(relation);
                if (!isSketchPatternedConstraint(relation)) {
                    return;
                }
                const sourcePatternPersistentId = relation.getParametersSnapshot().patternPersistentId;
                const remappedPatternPersistentId = persistentIdMap.get(sourcePatternPersistentId);
                if (!remappedPatternPersistentId) {
                    throw new Error(`草图复制失败：Patterned 关系引用的圆周阵列不存在 ${sourcePatternPersistentId}。`);
                }
                relation.remapPatternPersistentId(remappedPatternPersistentId);
            });
        });
    }

    /**
     * 收集新逻辑草图换身份时必须覆盖的全部领域对象。
     *
     * 设计说明：
     * 1. 普通对象由草图组跟踪集合提供；
     * 2. 槽口接缝点、圆心等关联点可能只被边对象引用而未直接挂在 Object3D 子树；
     * 3. 智能尺寸可以直接引用这些点，因此必须一并换新并纳入临时映射。
     *
     * @returns 去重后的草图领域对象列表。
     */
    private collectPersistentIdentityDomainObjects(): ISketchObject[] {
        const domainObjects = new Set<ISketchObject>(this._sketchObjectSet);
        this._sketchReferenceTargetSet.forEach((referenceTarget) => {
            domainObjects.add(referenceTarget);
        });

        this._sketchObjectSet.forEach((trackedObject) => {
            if (!isAbstractSketchEdgeObject(trackedObject)) {
                return;
            }
            trackedObject.relatedSketchPoints.forEach((relatedPoint) => {
                domainObjects.add(relatedPoint);
            });
        });

        return Array.from(domainObjects);
    }


    private updateSetByAction<T extends THREE.Object3D>(set: Set<T>, object: T, action: SketchGroupTrackAction): void {
        if (action === SketchGroupTrackAction.Add) {
            set.add(object);
            return;
        }
        set.delete(object);
    }

    /**
     * 清空所有草图对象跟踪集合。
     */
    private clearTrackedSets(): void {
        this._sketchObjectSet.clear();
        this._sketchPointObjectSet.clear();
        this._endPointSet.clear();
        this._midPointSet.clear();
        this._centerPointSet.clear();
        this._quadrantPointSet.clear();
        this._sketchPointKindSet.clear();
        this._sketchEdgeObjectSet.clear();
        this._sketchLineObjectSet.clear();
        this._sketchArcObjectSet.clear();
        this._sketchCircleObjectSet.clear();
        this._sketchEllipseObjectSet.clear();
        this._sketchSplineCurveObjectSet.clear();
        this._sketchSlotObjectSet.clear();
        this._sketchCollectionObjectSet.clear();

        this._persistentIdIndexMap.clear();
        this._sketchReferenceTargetSet.clear();
        this._referenceTargetPersistentIdIndexMap.clear();

    }
}
